import React, { useCallback, useEffect, useRef, useState } from 'react';
import { BERRY, BORDER, FONT_HEAD, FONT_MONO, INK, LABEL, MUTED } from './organizerSpace';
import {
  BANNER_ASPECT, BANNER_OUTPUT_WIDTH, clampOffset, coverScale, sourceRect,
} from './bannerCrop';

/** Serverseitige Grenze (ImageBytes.MAX_BYTES). Hier gespiegelt, damit der Hinweis vor dem Upload kommt. */
const MAX_BYTES = 2 * 1024 * 1024;

/** Unter dieser Breite wird ein Banner auf großen Bildschirmen sichtbar unscharf. */
const BANNER_MIN_WIDTH = 900;

/**
 * Ab diesem Seitenverhältnis verliert ein Logo im Kreis spürbar an Größe.
 *
 * 1.7 und nicht 2.2: ein Bildschirmfoto im Format 1179×630 (Verhältnis 1.87) schrumpft im Kreis
 * schon auf ein Drittel der Fläche — genau der Fall, in dem der Hinweis gebraucht wird.
 */
const LOGO_MAX_RATIO = 1.7;

const MAX_ZOOM = 3;

interface Props {
  kind: 'logo' | 'banner';
  file: File;
  organizerName: string;
  uploading: boolean;
  /** Bekommt die tatsächlich hochzuladende Datei — beim Banner den gewählten Ausschnitt. */
  onConfirm: (file: File) => void;
  onCancel: () => void;
  onPickOther: () => void;
}

const CSS = `
.au-backdrop { position:fixed; inset:0; z-index:70; background:rgba(44,44,44,.45);
  display:flex; align-items:center; justify-content:center; padding:16px; }
.au-sheet { width:100%; max-width:640px; max-height:90vh; overflow-y:auto; background:#fff;
  border-radius:20px; padding:26px 26px 20px; }
.au-eyebrow { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.14em; text-transform:uppercase;
  color:${BERRY}; margin:0 0 8px; }
.au-title { font-family:${FONT_HEAD}; font-weight:800; font-size:23px; letter-spacing:-.01em;
  color:${INK}; margin:0 0 6px; }
.au-lead { font-size:14px; color:${MUTED}; line-height:1.55; margin:0 0 18px; }

.au-stage { border:1px solid ${BORDER}; border-radius:14px; padding:18px; margin-bottom:14px;
  background:repeating-conic-gradient(#F0EDED 0% 25%, #fff 0% 50%) 50% / 18px 18px; }

/* Der Rahmen hat das Seitenverhältnis des echten Banners, nicht das des Dialogs — sonst zeigt
   die Vorschau einen anderen Ausschnitt als die Seite. */
.au-frame { position:relative; width:100%; aspect-ratio:${BANNER_ASPECT}; border-radius:14px;
  overflow:hidden; background:#EDE8E8; touch-action:none; cursor:grab; user-select:none; }
.au-frame--dragging { cursor:grabbing; }
.au-frame img { position:absolute; top:0; left:0; transform-origin:0 0; max-width:none;
  pointer-events:none; -webkit-user-drag:none; }

/* Zwei Größen nebeneinander.
   Groß, damit man das Bild überhaupt beurteilen kann — und daneben die echten 92px, weil genau
   die auf der Seite stehen. Nur die große wäre geschmeichelt, nur die kleine (so war es zuerst)
   ist zu klein, um etwas zu erkennen. */
/* Unten ausgerichtet, damit die beiden Beschriftungen auf einer Linie stehen — mittig zentriert
   säßen sie auf verschiedenen Höhen und läsen sich wie zwei getrennte Dinge. */
.au-logos { display:flex; align-items:flex-end; justify-content:center; gap:28px; flex-wrap:wrap; }
.au-logo-col { display:flex; flex-direction:column; align-items:center; gap:9px; }
.au-logo-cap { font-family:${FONT_MONO}; font-size:9.5px; letter-spacing:.1em; text-transform:uppercase;
  color:${LABEL}; }
.au-logo { border-radius:50%; overflow:hidden; border:1px solid ${BORDER}; background:#fff;
  display:flex; align-items:center; justify-content:center; }
.au-logo--big { width:190px; height:190px; }
.au-logo--real { width:92px; height:92px; }
.au-logo img { width:100%; height:100%; object-fit:contain; box-sizing:border-box; }
.au-logo--big img { padding:23px; }
.au-logo--real img { padding:11px; }
@media (max-width:520px) { .au-logo--big { width:150px; height:150px; } .au-logos { gap:18px; } }

.au-zoom { display:flex; align-items:center; gap:12px; margin:0 0 14px; }
.au-zoom label { font-family:${FONT_MONO}; font-size:10px; letter-spacing:.1em; text-transform:uppercase;
  color:${LABEL}; white-space:nowrap; }
.au-zoom input[type=range] { flex:1; accent-color:${BERRY}; }
.au-reset { font-family:inherit; font-size:13px; font-weight:700; color:${BERRY}; background:none;
  border:0; cursor:pointer; padding:6px 4px; white-space:nowrap; }
.au-reset:disabled { color:${MUTED}; cursor:default; }

.au-facts { display:flex; flex-wrap:wrap; gap:8px; margin:0 0 14px; padding:0; list-style:none; }
.au-fact { font-family:${FONT_MONO}; font-size:11px; letter-spacing:.04em; color:${LABEL};
  background:#F5F3F3; border-radius:7px; padding:6px 9px; }
.au-note { font-size:13.5px; line-height:1.55; border-radius:10px; padding:11px 13px; margin:0 0 12px; }
.au-note--warn { background:rgba(217,159,26,.14); color:#7A5B10; }
.au-note--stop { background:rgba(178,45,45,.12); color:#8E2020; }
.au-actions { display:flex; flex-wrap:wrap; gap:10px; margin-top:6px; }
.au-btn { font-family:inherit; font-size:14px; font-weight:700; border-radius:10px; padding:11px 16px;
  cursor:pointer; border:1px solid ${BORDER}; background:#fff; color:${INK}; }
.au-btn--primary { background:${BERRY}; border-color:${BERRY}; color:#fff; }
.au-btn:disabled { opacity:.5; cursor:default; }
`;

const formatSize = (bytes: number) =>
  bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.round(bytes / 1024)} KB`;

/**
 * Zeigt das gewählte Bild, bevor es hochgeladen wird — beim Banner mit wählbarem Ausschnitt.
 *
 * Zwei Gründe für den Zwischenschritt. Der Veranstalter sieht **in der echten Form**, was er da
 * einstellt — ein breites Logo wird im Kreis klein, ein schmaler Banner auf einem großen Monitor
 * unscharf. Und jeder Upload kostet die Redaktion eine Entscheidung: was hier schon auffällt,
 * muss dort nicht mehr abgelehnt werden.
 *
 * **Der Ausschnitt wird in die Datei gerechnet**, nicht als Koordinaten gespeichert. Sonst müsste
 * jede Stelle, die den Banner zeigt (Seite, App, später vielleicht eine Mail), dieselbe Rechnung
 * noch einmal machen und identisch treffen. So liegt im Speicher genau das Bild, das alle zeigen.
 *
 * Das Logo bleibt ohne Zuschnitt: ein Wappen anzuschneiden ist genau das, was wir nicht wollen.
 */
const AssetUploadPreview: React.FC<Props> = ({
  kind, file, organizerName, uploading, onConfirm, onCancel, onPickOther,
}) => {
  const [url, setUrl] = useState<string | null>(null);
  const [size, setSize] = useState<{ w: number; h: number } | null>(null);
  const [zoom, setZoom] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [preparing, setPreparing] = useState(false);

  const frameRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const dragStart = useRef<{ px: number; py: number; ox: number; oy: number } | null>(null);

  useEffect(() => {
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    const img = new Image();
    img.onload = () => setSize({ w: img.naturalWidth, h: img.naturalHeight });
    img.src = objectUrl;
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    document.body.classList.add('modal-open');
    return () => {
      document.body.style.overflow = prev;
      document.body.classList.remove('modal-open');
    };
  }, []);

  const frame = () => {
    const el = frameRef.current;
    return el ? { w: el.clientWidth, h: el.clientHeight } : { w: 0, h: 0 };
  };

  const scaleOf = useCallback((z: number) => {
    const f = frame();
    if (!size || !f.w) return 1;
    return coverScale(f.w, f.h, size.w, size.h) * z;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  /** Mittet das Bild im Rahmen — der Zustand, in dem jeder Zuschnitt beginnt. */
  const center = useCallback((z: number) => {
    const f = frame();
    if (!size || !f.w) return { x: 0, y: 0 };
    const s = scaleOf(z);
    return {
      x: clampOffset((f.w - size.w * s) / 2, f.w, size.w * s),
      y: clampOffset((f.h - size.h * s) / 2, f.h, size.h * s),
    };
  }, [size, scaleOf]);

  useEffect(() => {
    if (size) setOffset(center(zoom));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  const applyZoom = (next: number) => {
    if (!size) return;
    const f = frame();
    const oldS = scaleOf(zoom);
    const newS = scaleOf(next);
    // Um die Rahmenmitte zoomen: sonst wandert der gewählte Ausschnitt beim Schieben des Reglers weg.
    const cx = (f.w / 2 - offset.x) / oldS;
    const cy = (f.h / 2 - offset.y) / oldS;
    setZoom(next);
    setOffset({
      x: clampOffset(f.w / 2 - cx * newS, f.w, size.w * newS),
      y: clampOffset(f.h / 2 - cy * newS, f.h, size.h * newS),
    });
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (kind !== 'banner' || !size) return;
    (e.target as HTMLElement).setPointerCapture?.(e.pointerId);
    dragStart.current = { px: e.clientX, py: e.clientY, ox: offset.x, oy: offset.y };
    setDragging(true);
  };

  const onPointerMove = (e: React.PointerEvent) => {
    const start = dragStart.current;
    if (!start || !size) return;
    const f = frame();
    const s = scaleOf(zoom);
    setOffset({
      x: clampOffset(start.ox + (e.clientX - start.px), f.w, size.w * s),
      y: clampOffset(start.oy + (e.clientY - start.py), f.h, size.h * s),
    });
  };

  const endDrag = () => { dragStart.current = null; setDragging(false); };

  /**
   * Schneidet den gewählten Bereich auf eine Zeichenfläche und gibt ihn als Datei zurück.
   *
   * Weißer Grund und JPEG: ein Banner ist ein Foto, und ein PNG in dieser Größe reißt die 2-MB-
   * Grenze. Ohne den Grund würde ein transparentes PNG in JPEG schwarz.
   */
  const cropToFile = async (): Promise<File> => {
    const img = imgRef.current;
    const f = frame();
    if (!img || !size || !f.w) return file;
    const s = scaleOf(zoom);
    const r = sourceRect(f.w, f.h, s, offset.x, offset.y);
    // Nie größer rechnen als die Vorlage hergibt: aus einem 900px breiten Ausschnitt 1760px zu
    // machen erzeugt keine Schärfe, nur Dateigröße.
    const width = Math.max(600, Math.round(Math.min(BANNER_OUTPUT_WIDTH, r.sw)));
    const height = Math.round(width / BANNER_ASPECT);
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return file;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, r.sx, r.sy, r.sw, r.sh, 0, 0, width, height);
    const blob: Blob | null = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.9));
    if (!blob) return file;
    const name = file.name.replace(/\.[^.]+$/, '') || 'banner';
    return new File([blob], `${name}.jpg`, { type: 'image/jpeg' });
  };

  const [cropFailed, setCropFailed] = useState(false);

  /**
   * Schneidet zu und übergibt die Datei.
   *
   * Der Zuschnitt läuft über eine Zeichenfläche, und die kann fehlschlagen (zu große Fläche,
   * Speicher). Ohne dieses `catch` bliebe der Fehler eine unbehandelte Zusage: der Dialog schlösse
   * nicht, es käme keine Meldung, und der Upload fände nie statt — genau die Art Fehler, bei der
   * man später glaubt, das Hochladen habe funktioniert.
   */
  const confirm = async () => {
    if (kind !== 'banner') { onConfirm(file); return; }
    setPreparing(true);
    setCropFailed(false);
    try {
      onConfirm(await cropToFile());
    } catch {
      setCropFailed(true);
    } finally {
      setPreparing(false);
    }
  };

  const tooBig = file.size > MAX_BYTES;
  const wrongType = !['image/jpeg', 'image/png', 'image/webp'].includes(file.type);
  // Beim Banner entsteht ohnehin eine neue, kleinere Datei — die Größe der Vorlage zählt dort nicht.
  const blocked = wrongType || (tooBig && kind === 'logo');

  const thin = kind === 'banner' && !!size && size.w < BANNER_MIN_WIDTH;
  const wide = kind === 'logo' && !!size && Math.max(size.w / size.h, size.h / size.w) > LOGO_MAX_RATIO;
  const busy = uploading || preparing;
  const scale = scaleOf(zoom);

  return (
    <div className="au-backdrop" role="dialog" aria-modal="true"
      aria-label={kind === 'logo' ? 'Logo prüfen' : 'Banner zuschneiden'}
      onClick={e => { if (e.target === e.currentTarget && !busy) onCancel(); }}>
      <style>{CSS}</style>
      <div className="au-sheet">
        <p className="au-eyebrow">{kind === 'logo' ? 'Logo' : 'Banner'} für {organizerName}</p>
        <h2 className="au-title">
          {kind === 'banner' ? 'Wähle den Ausschnitt' : 'So sieht es auf deiner Seite aus'}
        </h2>
        <p className="au-lead">
          {kind === 'banner'
            ? 'Zieh das Bild an die richtige Stelle und stell die Größe ein — der Rahmen zeigt genau das, was später oben auf deiner Seite steht.'
            : 'Wenn das passt, laden wir es hoch.'}
          {' '}Es erscheint, sobald unsere Redaktion es kurz geprüft hat — dein bisheriges Bild
          bleibt so lange stehen.
        </p>

        <div className="au-stage">
          {url && (kind === 'banner' ? (
            <div
              ref={frameRef}
              className={`au-frame${dragging ? ' au-frame--dragging' : ''}`}
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={endDrag}
              onPointerCancel={endDrag}
            >
              <img
                ref={imgRef}
                src={url}
                alt="Vorschau des Banners"
                style={{
                  width: size ? size.w : undefined,
                  height: size ? size.h : undefined,
                  transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
                  transformOrigin: '0 0',
                }}
              />
            </div>
          ) : (
            <div className="au-logos">
              <div className="au-logo-col">
                <div className="au-logo au-logo--big"><img src={url} alt="Vorschau des Logos" /></div>
                <span className="au-logo-cap">Vergrößert</span>
              </div>
              <div className="au-logo-col">
                <div className="au-logo au-logo--real"><img src={url} alt="" /></div>
                <span className="au-logo-cap">So groß auf der Seite</span>
              </div>
            </div>
          ))}
        </div>

        {kind === 'banner' && (
          <div className="au-zoom">
            <label htmlFor="au-zoom-range">Größe</label>
            <input
              id="au-zoom-range"
              type="range"
              min={1}
              max={MAX_ZOOM}
              step={0.02}
              value={zoom}
              disabled={busy}
              onChange={e => applyZoom(Number(e.target.value))}
            />
            <button
              className="au-reset"
              onClick={() => { setZoom(1); setOffset(center(1)); }}
              disabled={busy || (zoom === 1 && offset.x === center(1).x && offset.y === center(1).y)}
            >
              Zurücksetzen
            </button>
          </div>
        )}

        <ul className="au-facts">
          <li className="au-fact">{file.name.length > 34 ? `${file.name.slice(0, 32)}…` : file.name}</li>
          {size && <li className="au-fact">{size.w} × {size.h} px</li>}
          <li className="au-fact">{formatSize(file.size)}</li>
        </ul>

        {tooBig && kind === 'logo' && (
          <p className="au-note au-note--stop">
            Die Datei ist zu groß — höchstens 2 MB. Verkleinere sie und wähle sie noch einmal aus.
          </p>
        )}
        {cropFailed && (
          <p className="au-note au-note--stop">
            Der Ausschnitt ließ sich nicht berechnen — das passiert bei sehr großen Bildern. Nimm
            eine kleinere Datei oder lade das Bild ohne Zuschnitt hoch.
          </p>
        )}
        {wrongType && (
          <p className="au-note au-note--stop">
            Dieses Format können wir nicht anzeigen. Nimm JPEG, PNG oder WebP.
          </p>
        )}
        {thin && !blocked && (
          <p className="au-note au-note--warn">
            Das Bild ist nur {size?.w} Pixel breit. Auf großen Bildschirmen wird der Banner damit
            unscharf — ab etwa {BANNER_MIN_WIDTH} Pixel sieht es überall sauber aus.
          </p>
        )}
        {wide && !blocked && (
          <p className="au-note au-note--warn">
            Das Logo ist deutlich breiter als hoch. Im runden Rahmen wird es dadurch klein. Ein
            quadratischer Ausschnitt — etwa nur das Wappen ohne Schriftzug — wirkt hier besser.
          </p>
        )}

        <div className="au-actions">
          <button className="au-btn au-btn--primary" onClick={confirm} disabled={blocked || busy}>
            {busy ? 'Wird hochgeladen…' : 'Hochladen'}
          </button>
          <button className="au-btn" onClick={onPickOther} disabled={busy}>Anderes Bild wählen</button>
          <button className="au-btn" onClick={onCancel} disabled={busy}>Abbrechen</button>
        </div>
      </div>
    </div>
  );
};

export default AssetUploadPreview;
